/**
 * 닉네임 변경 — 머리줄 사용자 메뉴 › 닉네임 변경.
 *
 * 2026-11-04 신고 반영:
 *  · 공용 창(AppModal)으로 — 예전 전용 CSS 는 다크 모드를 몰라 흰 창에 어두운 입력칸이 섞였다
 *  · 이메일이 없는 계정(Google · 네이버 · 체험 계정)은 「이메일 주소를 입력해주세요」 로 거절됐다 —
 *    이메일 경로(/api/emailverif/change-nickname)는 **이메일 계정의 공통 저장소**를 바꾸는 길이라서다.
 *    이메일이 없으면 테넌트 계정을 세션 사용자로 직접 고친다(qbase.saveUser update — 가입 마무리와 같은 길)
 *  · 중복 확인 — 「중복 확인」 단추, 그리고 저장 직전에 한 번 더(QBase: 같은 시스템의 다른 사용자, 대소문자·앞뒤 공백 무시).
 *    저장할 때도 서버가 다시 보고 409 로 거절한다(확인과 저장 사이에 남이 가져갈 수 있다)
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiAlertTriangle, FiCheckCircle, FiUser } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { AUTH_ACTION_TYPES } from '../redux/reducers/authReducer';
import { EMAIL_VERIF_API } from '../config/qbase';
import { emailVerifFetch, saveUser, checkNickname } from '../services/qbaseClient';
import { refreshMe as refreshForumName } from '../services/forum';
import { IS_STANDALONE } from '../config/features';
import './NicknameChangeModal.css';

const MIN = 2;
const MAX = 20;

const NicknameChangeModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);
    const current = (user && (user.nickname || user.name)) || '';
    const [value, setValue] = useState('');
    const [check, setCheck] = useState(null);       // null | 'checking' | 'available' | 'taken'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (isOpen) { setValue(current); setCheck(null); setError(''); setSuccess(''); }
    }, [isOpen]);   // eslint-disable-line react-hooks/exhaustive-deps -- 열 때만 현재 값으로 채운다

    const trimmed = value.trim();
    const validate = () => {
        if (!trimmed) return t('auth:nick.err.required');
        if (trimmed.length < MIN) return t('auth:nick.err.tooShort');
        if (trimmed.length > MAX) return t('auth:nick.err.tooLong');
        if (trimmed.toLowerCase() === current.trim().toLowerCase()) return t('auth:nick.err.same');
        return '';
    };

    const runCheck = async () => {
        const v = validate();
        if (v) { setError(v); return false; }
        setError(''); setCheck('checking');
        try {
            const r = await checkNickname(trimmed);
            const ok = !!(r && r.available);
            setCheck(ok ? 'available' : 'taken');
            if (!ok) setError(t('auth:nick.err.taken'));
            return ok;
        } catch (e) {
            setCheck(null);
            setError(e.message || t('auth:nick.err.unexpected'));
            return false;
        }
    };

    const applyLocal = (nickname) => {
        const updated = { ...user, nickname, name: nickname };
        dispatch({ type: AUTH_ACTION_TYPES.LOGIN_SUCCESS, payload: { user: updated, isTemporaryPassword: false } });
        localStorage.setItem('livesql_user', JSON.stringify(updated));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        // 확인하지 않았거나 확인 뒤 글을 바꿨으면 지금 확인한다
        if (check !== 'available' && !(await runCheck())) return;
        setLoading(true); setError(''); setSuccess('');
        try {
            // 화면·챌린지·포럼에 보이는 이름은 **로그인한 계정(테넌트)** 의 것이다 — 늘 그것을 세션 사용자로 고친다(중복이면 서버가 409).
            // 이메일 경로를 먼저 쓰면 이메일이 없거나(Google · 네이버) 공통 저장소에 없는 계정(체험 계정 등)이 「이메일 주소를 입력해주세요」 ·
            // 「등록되지 않은 이메일」 로 막혔다(신고 2026-11-04)
            await saveUser({ userKnme: trimmed, nicknameUnique: true }, 'update');
            // 이메일로 가입한 계정이면 공통 저장소의 닉네임도 맞춘다 — 실패해도 바꾼 이름은 그대로다(표시는 테넌트 값을 쓴다)
            if (user && user.email) {
                emailVerifFetch(EMAIL_VERIF_API.changeNickname, { email: user.email, newNickname: trimmed }).catch(() => {});
            }
            applyLocal(trimmed);
            // 포럼의 작성자 이름(지난 글 포함)도 곧바로 새 이름으로 — 실패해도 다음에 포럼에 들어오면 바뀐다
            if (!IS_STANDALONE) refreshForumName().catch(() => {});
            setSuccess(t('auth:nick.ok', { nickname: trimmed }));
            setTimeout(() => onClose(), 1500);
        } catch (err) {
            if (err && (err.status === 409 || (err.body && err.body.code === 'NICKNAME_ALREADY_EXISTS'))) {
                setCheck('taken'); setError(t('auth:nick.err.taken'));
            } else {
                setError((err && err.message) || t('auth:nick.err.unexpected'));
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;
    return (
        <AppModal open={!!isOpen} onClose={() => { if (!loading) onClose(); }} title={t('auth:nick.title')} icon={<FiUser size={14} />}
            className="app-modal-default nick-modal" closeLabel={t('auth:nick.close')}>
            <form className="nick-body" onSubmit={handleSubmit}>
                <div className="nick-current">
                    <span className="nick-label">{t('auth:nick.current')}</span>
                    <strong>{current || t('auth:nick.none')}</strong>
                </div>
                <label className="nick-label" htmlFor="nick-new">{t('auth:nick.new')}</label>
                <div className="nick-row">
                    <input id="nick-new" className="nick-input" value={value} maxLength={MAX} autoComplete="off" autoFocus
                        placeholder={t('auth:nick.newPlaceholder')} disabled={loading}
                        onChange={(e) => { setValue(e.target.value); setCheck(null); setError(''); setSuccess(''); }} />
                    <button type="button" className="app-btn" disabled={loading || check === 'checking' || !trimmed} onClick={runCheck}>
                        {check === 'checking' ? t('auth:nick.checking') : t('auth:nick.check')}
                    </button>
                </div>
                <div className="nick-hint">{t('auth:nick.lengthHint')}</div>
                {check === 'available' && !error ? <div className="nick-ok"><FiCheckCircle size={13} /> {t('auth:nick.available')}</div> : null}
                {error ? <div className="nick-error"><FiAlertTriangle size={13} /> {error}</div> : null}
                {success ? <div className="nick-ok"><FiCheckCircle size={13} /> {success}</div> : null}
                <div className="app-modal-footer nick-footer">
                    <span className="app-modal-footer-spacer" />
                    <button type="button" className="app-btn" onClick={onClose} disabled={loading}>{t('auth:nick.cancel')}</button>
                    <button type="submit" className="app-btn app-btn-primary" disabled={loading || !trimmed || check === 'taken'}>
                        {loading ? t('auth:nick.changing') : t('auth:nick.title')}
                    </button>
                </div>
            </form>
        </AppModal>
    );
};

export default NicknameChangeModal;
